const roundTo = (roundTo: number) => (date: number) =>
  Math.round(date / roundTo) * roundTo;

const roundDownTo = (roundTo: number) => (date: number) =>
  Math.floor(date / roundTo) * roundTo;

const roundUpTo = (roundTo: number) => (date: number) =>
  Math.ceil(date / roundTo) * roundTo;

export const roundTo5Minutes = roundTo(1000 * 60 * 5);
export const roundDownTo5Minutes = roundDownTo(1000 * 60 * 5);
export const roundDownTo10Minutes = roundDownTo(1000 * 60 * 10);
export const roundUpTo5Minutes = roundUpTo(1000 * 60 * 5);

export const roundDownToXMinutes = (date: number, minutes: number) => {
  return Math.floor(date / (minutes * 1000 * 60)) * (minutes * 1000 * 60);
};

export const formatDaysText = (days: number): string =>
  `${days} ${days === 1 ? "day" : "days"}`;

/**
 * `Mar 15, 2026` — a Stripe timestamp as a readable date.
 *
 * Takes **unix seconds**, matching `getRemainingDaysUntil` and the Stripe payloads
 * these dates come from. Local time, like every formatter here, so a set of dates
 * rendered together always agree with each other.
 */
export const formatDate = (unixSeconds: number): string =>
  new Date(unixSeconds * 1_000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/** `MAR 15 2026` — `formatDate` for surfaces that are uppercase throughout. */
export const formatDateUpper = (unixSeconds: number): string =>
  formatDate(unixSeconds).replace(",", "").toUpperCase();

/**
 * `3/15/26` — a compact numeric date for a dense row that still needs a year, such
 * as a scheduled cancellation on a module row or a finished subscription's period.
 *
 * `formatMonthDay` is the same idea for rows where the year is already obvious.
 */
export const formatShortDate = (unixSeconds: number): string =>
  new Date(unixSeconds * 1_000).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });

/** `8/17` — a compact date for a dense row, where the year is already obvious. */
export const formatMonthDay = (unixSeconds: number): string => {
  const date = new Date(unixSeconds * 1_000);

  return `${date.getMonth() + 1}/${date.getDate()}`;
};

import humanizeDuration from "humanize-duration";

const RELATIVE_LANGUAGE = "igUtilsRelative";

const compactRelative = humanizeDuration.humanizer({
  delimiter: " ",
  language: RELATIVE_LANGUAGE,
  languages: {
    [RELATIVE_LANGUAGE]: {
      d: () => "d",
      h: () => "h",
      m: () => "m",
      s: () => "s",
    },
  },
  largest: 1,
  round: true,
  spacer: "",
  units: ["d", "h", "m", "s"],
});

export type FormatRelativeTimeOptions = {
  fallback?: string | ((ts: number) => string);
  maxRelativeAgeMs?: number;
  now?: number;
};

export function formatRelativeDate(dateStr?: string | Date): string {
  if (!dateStr) {
    return "";
  }
  const date = new Date(dateStr);
  const parsedTs = date.getTime();
  if (Number.isNaN(parsedTs)) {
    return "Invalid Date";
  }

  return formatRelativeTime(parsedTs, {
    maxRelativeAgeMs: 7 * 24 * 60 * 60_000,
    fallback: (timestamp) =>
      new Date(timestamp).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
  });
}

export const formatRelativeTime = (
  ts: number | null | undefined,
  options: FormatRelativeTimeOptions = {}
): string => {
  if (ts == null) {
    if (options.fallback == null) {
      return "";
    }
    return typeof options.fallback === "string"
      ? options.fallback
      : options.fallback(options.now ?? Date.now());
  }

  const ago = (options.now ?? Date.now()) - ts;
  if (ago < 1000) {
    return "just now";
  }

  if (
    options.maxRelativeAgeMs !== undefined &&
    ago > options.maxRelativeAgeMs &&
    options.fallback !== undefined
  ) {
    return typeof options.fallback === "string"
      ? options.fallback
      : options.fallback(ts);
  }

  return `${compactRelative(ago)} ago`;
};

export type FormatLastUpdatedTextOptions = FormatRelativeTimeOptions & {
  justNowThresholdMs?: number;
  noPrefix?: boolean;
  prefix?: string;
};

const DEFAULT_LAST_UPDATED_PREFIX = "Last updated: ";
const DEFAULT_JUST_NOW_THRESHOLD_MS = 30 * 1000;

const resolveFallback = (
  fallback: FormatRelativeTimeOptions["fallback"],
  ts: number
): string => {
  if (fallback == null) {
    return "";
  }
  return typeof fallback === "string" ? fallback : fallback(ts);
};

export const formatLastUpdatedText = (
  ts: number | null | undefined,
  options: FormatLastUpdatedTextOptions = {}
): string => {
  const prefix = options.noPrefix
    ? ""
    : (options.prefix ?? DEFAULT_LAST_UPDATED_PREFIX);
  const now = options.now ?? Date.now();

  if (ts == null) {
    const fallback = resolveFallback(options.fallback, now);
    return fallback ? `${prefix}${fallback}` : "";
  }

  const label =
    now - ts < (options.justNowThresholdMs ?? DEFAULT_JUST_NOW_THRESHOLD_MS)
      ? "just now"
      : formatRelativeTime(ts, {
          fallback: options.fallback,
          maxRelativeAgeMs: options.maxRelativeAgeMs,
          now,
        });

  return `${prefix}${label}`;
};
